import React, { createContext, useContext, useEffect, useState } from 'react'

import {
  fetchAndActivate,
  getAll,
  getRemoteConfig,
  type RemoteConfig,
} from 'firebase/remote-config'

import { app } from '@web-app/lib/firebase'

interface FirebaseRemoteConfigContextType {
  remoteConfig: Record<string, string>
  loading: boolean
}

export const FirebaseRemoteConfigContext = createContext<
  FirebaseRemoteConfigContextType | undefined
>(undefined)

interface FirebaseRemoteConfigProviderProps {
  children: React.ReactNode
}

export const FirebaseRemoteConfigProvider: React.FC<
  FirebaseRemoteConfigProviderProps
> = ({ children }) => {
  const [remoteConfig, setRemoteConfig] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (typeof window === 'undefined') return

    let remoteConfigInstance: RemoteConfig

    const fetchRemoteConfig = async () => {
      try {
        remoteConfigInstance = getRemoteConfig(app)
        remoteConfigInstance.settings.minimumFetchIntervalMillis = 300000

        await fetchAndActivate(remoteConfigInstance)

        const allValues = getAll(remoteConfigInstance)
        const parsed: Record<string, string> = {}
        for (const [key, value] of Object.entries(allValues)) {
          parsed[key] = value.asString()
        }
        setRemoteConfig(parsed)
      } catch (error) {
        console.error('Failed to fetch Firebase Remote Config:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchRemoteConfig()
  }, [])

  return (
    <FirebaseRemoteConfigContext.Provider value={{ remoteConfig, loading }}>
      {children}
    </FirebaseRemoteConfigContext.Provider>
  )
}

export const useFirebaseRemoteConfig = () => {
  const context = useContext(FirebaseRemoteConfigContext)
  if (context === undefined) {
    throw new Error(
      'useFirebaseRemoteConfig must be used within a FirebaseRemoteConfigProvider',
    )
  }
  return context
}
